if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.dataset.mediaFilterOption) {
          this.optionValueSelectionChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.onMediaFilterOptionChange.bind(this)
          );
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onMediaFilterVariantChange.bind(this)
          );
          this.filterMedia(this.dataset.mediaFilterActive);
          window.requestAnimationFrame(() => this.filterMedia(this.dataset.mediaFilterActive));
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.optionValueSelectionChangeUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      onMediaFilterOptionChange({ data: { event, target } }) {
        if (!this.closest('product-info')?.contains(event.target)) return;
        if (target.dataset.optionName !== this.dataset.mediaFilterOption) return;
        this.filterMedia(target.value);
      }

      onMediaFilterVariantChange({ data: { sectionId } }) {
        const productInfo = this.closest('product-info');
        if (!productInfo || productInfo.dataset.section !== sectionId) return;

        const selectedControl = Array.from(productInfo.querySelectorAll('[data-option-name]')).find((control) => {
          if (control.dataset.optionName !== this.dataset.mediaFilterOption) return false;
          return control.matches('input:checked, option[selected]');
        });
        if (selectedControl) this.filterMedia(selectedControl.value);
      }

      filterMedia(selectedGroup) {
        if (!selectedGroup) return;

        const normalize = (value) => value.trim().toLocaleLowerCase();
        const activeGroup = normalize(selectedGroup);
        const viewerItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-group]'));
        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('[data-media-group]') || []);

        // Hidden model media must never re-enter Dawn's scroll-reveal cycle.
        // IntersectionObserver can otherwise flash it, hide it and fade it in again.
        viewerItems.forEach((item) => {
          item.classList.remove('scroll-trigger', 'animate--fade-in', 'scroll-trigger--offscreen');
          item.style.opacity = '1';
          item.style.animation = 'none';
        });

        [...viewerItems, ...thumbnailItems].forEach((item) => {
          item.hidden = normalize(item.dataset.mediaGroup) !== activeGroup;
        });

        const visibleMedia = viewerItems.filter((item) => !item.hidden);
        const currentMedia = viewerItems.find((item) => item.classList.contains('is-active'));
        if (visibleMedia.length && (!currentMedia || currentMedia.hidden)) {
          this.setActiveMedia(visibleMedia[0].dataset.mediaId, false);
        }

        this.elements.viewer.slider?.scrollTo({ left: 0 });
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();

        const counterCurrent = this.elements.viewer.querySelector('.slider-counter--current');
        const counterTotal = this.elements.viewer.querySelector('.slider-counter--total');
        if (counterCurrent) counterCurrent.textContent = '1';
        if (counterTotal) counterTotal.textContent = visibleMedia.length;

        const productInfo = this.closest('product-info');
        const mediaGroups = new Map(
          viewerItems.map((item) => [item.dataset.mediaId.split('-').pop(), normalize(item.dataset.mediaGroup)])
        );
        productInfo?.querySelectorAll('product-modal [data-media-id]').forEach((item) => {
          const itemGroup = mediaGroups.get(item.dataset.mediaId);
          if (itemGroup) item.hidden = itemGroup !== activeGroup;
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
